import styled from '@emotion/styled';
import { useEventLogging } from '@innovamat/event-logging';
import { MenuItem, MenuSection } from '@innovamat/glimmer-components';
import type { IconType } from '@innovamat/glimmer-icons';
import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useNotifications } from '../../../notifications/notifications-provider';
import { useSaveTeacherAccess } from '../../../user-management/hooks';
import { useCurrentNavigationValues, useNavigation } from '../../hooks';
import type { ProcessedSectionItem, ProcessedSitemapTree } from '../../types';
import { openSectionInNewTab } from '../../utils';
import { LateralMenuSkeleton } from './lateral-menu-skeleton';
import { NotificationDotComponent } from './notification-dot';

type LateralMenuProps = {
  sitemapTree?: ProcessedSitemapTree;
  isMobile?: boolean;
  onNavigate?: () => void;
};

const MenuContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
`;

export function LateralMenu({
  sitemapTree,
  isMobile,
  onNavigate,
}: LateralMenuProps): JSX.Element {
  const { menu, submenu } = useParams();
  const { pathname } = useLocation();
  const { navigateInSameNavType } = useNavigation();
  const { getTypeOfPagePermission, currentClassroom, currentCourse } =
    useCurrentNavigationValues();
  const actualRoute = submenu ? `/${menu}/${submenu}` : `/${menu}`;
  const { setEventData } = useEventLogging();
  const [selected, setSelected] = useState(actualRoute);
  const { sessionDoneNotification } = useNotifications();
  const { onSaveTeacherAccess } = useSaveTeacherAccess();

  const [openSections, setOpenSections] = useState<string[]>([]);

  const handleSectionClick = (value: string): void => {
    if (openSections.includes(value)) {
      setOpenSections(openSections.filter((section) => section !== value));
    } else {
      setOpenSections([...openSections, value]);
    }
  };

  const handleNavigateTo = (
    section: ProcessedSectionItem,
    openNewTab?: boolean
  ): void => {
    onNavigate?.();

    if (!section || typeof section === 'string') return;

    if (currentClassroom?.id) {
      onSaveTeacherAccess(currentClassroom.id);
    }

    const pageId = section.fullPageId || section.demoPageId;

    if (pageId) {
      setEventData('change_page', {
        page_id: pageId,
        page_name: section.value,
        page_type: getTypeOfPagePermission(section),
      });
    } else {
      setEventData('change_menu', {
        item_name: section.route,
        classroom_id: currentClassroom?.id,
        course: currentCourse?.order,
      });
    }

    if (openNewTab) {
      openSectionInNewTab(section, pathname);
    } else {
      setSelected(section.route as string);
      navigateInSameNavType(section.route as string);
    }
  };

  useEffect(() => {
    if (!sitemapTree) return;
    setSelected(actualRoute);

    setOpenSections(() => {
      const section = sitemapTree?.find((section) => {
        if (section && 'items' in section) {
          return section.items?.find(
            (item) => typeof item !== 'string' && item.route === actualRoute
          );
        }
        return false;
      });
      return [...openSections, section?.value!];
    });

    return () => {
      setOpenSections([]);
    };
  }, [sitemapTree, pathname, actualRoute]);

  if (sitemapTree === undefined || sitemapTree.length === 0) {
    return <LateralMenuSkeleton />;
  }

  return (
    <MenuContainer>
      {sitemapTree?.map((section, index) => {
        if (!section) return null;
        if (section.menuItemType === 'item') {
          return (
            <MenuItem
              key={section.id}
              text={section.value}
              subtleText={section.subtle}
              icon={section.icon as IconType}
              state={selected === section.route ? 'selected' : 'active'}
              onClick={() => {
                handleNavigateTo(section);
                if (index === 0 && sessionDoneNotification) {
                  setEventData(
                    'teacher_control_click_general_view_with_notification',
                    {}
                  );
                }
              }}
              size={isMobile ? 'XXL' : 'L'}
              dataTestId={section.route}
              leftContent={
                index === 0 && sessionDoneNotification ? (
                  <NotificationDotComponent />
                ) : undefined
              }
              dataAttributes={section.dataAttributes}
            />
          );
        }

        return (
          <MenuSection
            dataTestId={`menu-section-${index}`}
            key={section.id}
            text={section.value}
            icon={section.icon as IconType}
            size={isMobile ? 'L' : 'M'}
            isOpen={openSections.includes(section.value)}
            onClick={() => handleSectionClick(section.value)}
            content={
              section.items
                ? section.items.map((item) => {
                    if (typeof item === 'string') {
                      return item;
                    }

                    return {
                      id: item.id,
                      text: item.value,
                      subtleText: item.subtle,
                      rightIcon: item.openNewTab ? 'OpenNewTabIcon' : undefined,
                      rightIconSize: 'S',
                      state: selected === item.route ? 'selected' : 'active',
                      onClick: () => handleNavigateTo(item, item.openNewTab),
                      dataTestId: item.route,
                      dataAttributes: item.dataAttributes,
                    };
                  })
                : []
            }
          />
        );
      }) || []}
    </MenuContainer>
  );
}
